<template>
    <section class="section manage-events-app section-mobile-padding is-full-height-with-navbar">
        <div class="container">
            <div class="columns">
                <div class="column is-full has-text-centered">
                    <h1 class="title">{{ translate("page.events.manage") }}</h1>
                </div>
            </div>

            <div class="columns is-centered is-multiline is-mobile">
                <template v-if="initDone">
                    <div class="column is-10">
                        <div class="buttons">
                            <button
                                class="button is-primary plausible-event-name=[Organizer]+AddNewDraftEvent"
                                :disabled="!canCreateInSelectedFederation"
                                @click="addNewDraftEvent"
                            >
                                <span class="icon">
                                    <i class="fa fa-plus" />
                                </span>
                                <span>
                                    {{ translate("page.events.add_event") }}
                                </span>
                            </button>

                            <a
                                :disabled="userRole === 'techAccount' || null"
                                class="button is-info"
                                :href="userRole !== 'techAccount' ? urls.notificationCenter().notificationListingPage() : '#'"
                            >
                                <span class="icon">
                                    <i class="fas fa-envelope"></i>
                                </span>
                                <span>{{ translate("notification_center.listing.main_title") }} </span>
                            </a>

                            <div class="mb-2 federation-select-wrapper" v-if="userRole !== 'techAccount'">
                                <FederationSelect
                                    class="federation-select"
                                    :remember-selection-key="SELECTED_FEDERATION_KEY"
                                    v-model="selectedFederation"
                                    @new-federation-click="onNewFederationClick"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="column is-10">
                        <QueryInput :placeholder="translate('common.find_event')" v-model="searchQuery" />
                    </div>

                    <template v-if="hasAnyEvents && !reloadingFederationChoice">
                        <div class="column is-10">
                            <template v-if="incomingEvents.length > 0">
                                <h4 class="mb-2 has-text-weight-bold">
                                    {{ translate("page.events.incoming") }}
                                </h4>
                                <EventsList :events="incomingEvents" :event-admin-url="eventAdminUrl" :search-query="searchQuery" />
                            </template>

                            <template v-if="draftEvents.length > 0">
                                <h4 class="mb-2 has-text-weight-bold">
                                    {{ translate("page.events.drafts") }}
                                </h4>
                                <EventsList
                                    :events="draftEvents"
                                    :event-admin-url="eventAdminUrl"
                                    :search-query="searchQuery"
                                    :limit="showAllDrafts ? undefined : DRAFT_EVENTS_PREVIEW_LIMIT"
                                    @load-all="showAllDrafts = true"
                                />
                            </template>

                            <h4 class="mb-2 has-text-weight-bold">
                                {{ translate("page.events.archive") }}
                            </h4>
                            <template v-if="!loadedArchive">
                                <div class="has-text-centered mt-3 mb-4">
                                    <button class="button" @click="loadArchiveEvents">
                                        <span class="icon">
                                            <i class="fas fa-sync-alt"></i>
                                        </span>
                                        <span>
                                            {{ translate("page.events.load_archive") }}
                                        </span>
                                    </button>
                                </div>
                            </template>
                            <template v-else-if="loadedArchive">
                                <EventsList :events="pastEvents" :event-admin-url="eventAdminUrl" :search-query="searchQuery" />
                            </template>
                        </div>
                    </template>
                    <template v-else-if="reloadingFederationChoice">
                        <div class="column is-10 is-flex is-justify-content-center">
                            <LoadingSpinner2 class="has-added-margin" />
                        </div>
                    </template>
                    <div v-else class="column is-10">
                        <!-- New organizer with unapproved federation - simplified onboarding -->
                        <div v-if="showNewOrganizerOnboarding" class="box has-text-centered empty-state-box">
                            <div class="empty-state-icon mb-4">
                                <span class="icon is-large has-text-primary">
                                    <i class="fas fa-calendar-plus fa-3x"></i>
                                </span>
                            </div>
                            <h2 class="title is-5 mb-3">
                                {{ translate("page.events.no_events_new_organizer_title") }}
                            </h2>
                            <p class="subtitle is-6 mb-4">
                                {{ translate("page.events.no_events_new_organizer_subtitle") }}
                            </p>
                            <button
                                id="createFirstEventButton"
                                class="button is-primary mb-5 plausible-event-name=[Organizer]+CreateFirstEvent"
                                :disabled="!canCreateInSelectedFederation"
                                @click="addNewDraftEvent"
                            >
                                <span class="icon">
                                    <i class="fa fa-plus" />
                                </span>
                                <span>
                                    {{ translate("page.events.create_first_event") }}
                                </span>
                            </button>

                            <!-- Help center links -->
                            <EventHelpLinks />
                        </div>

                        <!-- Federation selected with no events - simple empty state -->
                        <div v-else-if="selectedFederation" class="box has-text-centered empty-state-box">
                            <div class="empty-state-icon mb-4">
                                <span class="icon is-large has-text-primary">
                                    <i class="fas fa-calendar-plus fa-3x"></i>
                                </span>
                            </div>
                            <h2 class="title is-5 mb-3">
                                {{ translate("page.events.no_events_approved_title") }}
                            </h2>
                            <p class="subtitle is-6 mb-4">
                                {{ translate("page.events.no_events_approved_subtitle") }}
                            </p>
                            <button
                                class="button is-primary mb-5 plausible-event-name=[Organizer]+CreateFirstEvent"
                                :disabled="!canCreateInSelectedFederation"
                                @click="addNewDraftEvent"
                            >
                                <span class="icon">
                                    <i class="fa fa-plus" />
                                </span>
                                <span>
                                    {{ translate("page.events.create_first_event") }}
                                </span>
                            </button>

                            <!-- Help center links -->
                            <EventHelpLinks />
                        </div>

                        <!-- No federation selected -->
                        <div v-else class="box has-text-centered empty-state-box">
                            <div class="empty-state-icon mb-4">
                                <span class="icon is-large has-text-grey-light">
                                    <i class="fas fa-calendar-alt fa-3x"></i>
                                </span>
                            </div>
                            <p class="help has-text-grey">
                                {{ translate("page.events.select_federation_first") }}
                            </p>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <LoadingSpinner2 class="has-added-margin" />
                </template>
            </div>
        </div>

        <FederationCreateModal ref="federationCreateModal" @created="onFederationCreated" />
        <MigrateToFederationModal ref="migrateToFederationModal" @migrated="fetchEventsToManageWithDebounce(selectedFederation)" />
    </section>
</template>

<script setup lang="ts">
    import { ref, onMounted, watch, computed } from "vue";
    import EventsList from "./EventsList.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import QueryInput from "../Common/QueryInput.vue";
    import eventsApi from "../Api/events";
    import { handleError } from "../Common/errorHandler";
    import { translate } from "../Common/translations";
    import urls from "../Urls/urls";
    import { CompactEvent, UserRole } from "../Common/types";
    import FederationSelect from "../Federations/Common/FederationSelect.vue";
    import { errorToast } from "../Common/toasts";
    import localStorage from "../Common/store";
    import debounce from "lodash.debounce";
    import FederationCreateModal from "../Federations/Listing/FederationCreateModal.vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import MigrateToFederationModal from "./MigrateToFederationModal.vue";
    import { manageEventsAppEventBus, OPEN_MIGRATE_TO_FEDERATION_MODAL } from "./manageEventsApp";
    import { useFederationStore } from "../Federations/Listing/federationStore";
    import { FederationRoleEnum } from "../Api/federationsApi";
    import EventHelpLinks from "./EventHelpLinks.vue";
    import { useScrollResetOnMount } from "../Common/useScrollResetOnMount";
    const props = defineProps<{ eventAdminUrl: string; userRole: UserRole | null }>();

    useScrollResetOnMount();

    const federationStore = useFederationStore();

    const initDone = ref<boolean>(false);
    const hasAnyEvents = ref<boolean>(false);
    const incomingEvents = ref<CompactEvent[]>([]);
    const draftEvents = ref<CompactEvent[]>([]);
    const pastEvents = ref<CompactEvent[]>([]);
    const canCreateEvents = ref<boolean>(false);
    const loadedArchive = ref<boolean>(false);
    const DRAFT_EVENTS_PREVIEW_LIMIT = 10;
    const showAllDrafts = ref<boolean>(false);
    const searchQuery = ref<string>("");
    const selectedFederation = ref<string | undefined>(undefined);
    const canCreateInSelectedFederation = computed(() => {
        if (!selectedFederation.value) {
            return canCreateEvents.value;
        }

        if (!canCreateEvents.value) {
            return false;
        }

        if (props.userRole === "admin") {
            return true;
        }

        const federation = federationStore.userFederations.find(item => item.id === selectedFederation.value);
        return federation?.federationRole === FederationRoleEnum.Owner || federation?.federationRole === FederationRoleEnum.FullOrganizer;
    });

    const fetchEventsToManage = (federationId?: string): Promise<void> => {
        return eventsApi
            .getEventsToManageForOrganizer(federationId, !federationId)
            .then(response => {
                incomingEvents.value = response.incomingEvents;
                draftEvents.value = response.draftEvents;
                showAllDrafts.value = false;
                pastEvents.value = response.pastEvents;
                hasAnyEvents.value = response.hasAnyEvents;
                canCreateEvents.value = response.canCreateEvents;
            })
            .catch(handleError);
    };

    const addNewDraftEvent = (): void => {
        if (!selectedFederation.value) {
            errorToast(translate("federation.select_federation_before_creating_draft_event"));
            return;
        }
        if (!canCreateInSelectedFederation.value) {
            return;
        }
        window.location.href = urls.eventAdmin().createEventWizardPage(selectedFederation.value);
    };

    const loadArchiveEvents = (): void => {
        loadedArchive.value = true;
    };

    watch(searchQuery, (): void => {
        loadArchiveEvents();
    });

    const reloadingFederationChoice = ref(false);

    function fetchEventsToManageWithDebounce(federationId?: string) {
        reloadingFederationChoice.value = true;
        debounce(() => {
            return fetchEventsToManage(federationId).finally(() => {
                reloadingFederationChoice.value = false;
            });
        }, 100)();
    }

    const SELECTED_FEDERATION_KEY = "manageEventsSelectedFederation";

    const showNewOrganizerOnboarding = computed(() => {
        return federationStore.shouldShowNewOrganizerOnboarding(hasAnyEvents.value);
    });

    watch(selectedFederation, newVal => {
        const selectedFederationId = localStorage.getString(SELECTED_FEDERATION_KEY);

        if (newVal === selectedFederationId) {
            return;
        }
        fetchEventsToManageWithDebounce(newVal);
    });

    onMounted(() => {
        // Check for federationId query parameter first
        const urlParams = new URLSearchParams(window.location.search);
        const federationIdFromUrl = urlParams.get("federationId");

        // Ensure federations are loaded (uses cache if FederationSelect already loaded)
        return federationStore
            .fetchFederationsForUser()
            .then(() => {
                let finalFederationId: string | undefined;

                // Priority 1: URL parameter (explicit user navigation)
                if (federationIdFromUrl) {
                    finalFederationId = federationIdFromUrl;
                    selectedFederation.value = federationIdFromUrl;
                    localStorage.setString(SELECTED_FEDERATION_KEY, federationIdFromUrl);
                }
                // Priority 2: Single unapproved federation (new organizer)
                else {
                    const singleUnapproved = federationStore.singleUnapprovedFederation;
                    if (singleUnapproved) {
                        finalFederationId = singleUnapproved.id;
                        selectedFederation.value = singleUnapproved.id;
                        localStorage.setString(SELECTED_FEDERATION_KEY, singleUnapproved.id);
                    }
                    // Priority 3: localStorage
                    else {
                        finalFederationId = localStorage.getString(SELECTED_FEDERATION_KEY);
                    }
                }

                // Fetch events with the determined federation
                return fetchEventsToManage(finalFederationId);
            })
            .finally(() => {
                initDone.value = true;
            });
    });

    // Handling new federation creation click

    const federationCreateModal = ref<ComponentExposed<typeof FederationCreateModal> | null>(null);

    function onNewFederationClick() {
        federationCreateModal.value?.open();
    }

    function onFederationCreated(federationId: string) {
        selectedFederation.value = federationId;
        localStorage.setString(SELECTED_FEDERATION_KEY, federationId);
        fetchEventsToManageWithDebounce(federationId);
    }

    //Migrate to federation modal
    const migrateToFederationModal = ref<ComponentExposed<typeof MigrateToFederationModal> | null>(null);

    function openMigrateToFederationModal(eventId: number) {
        migrateToFederationModal.value?.open(eventId);
    }

    onMounted(() => {
        manageEventsAppEventBus.$on(OPEN_MIGRATE_TO_FEDERATION_MODAL, (eventId: number) => {
            openMigrateToFederationModal(eventId);
        });
    });
</script>

<style scoped>
    .empty-state-box {
        padding: 3rem 2rem;
        border: 2px dashed #dbdbdb;
    }

    .empty-state-icon {
        opacity: 0.9;
    }

    @media screen and (max-width: 768px) {
        .empty-state-box {
            padding: 2rem 1.5rem;
        }
    }
</style>
